import { useEffect, useRef, useState } from 'react';
import { Download, Printer } from 'lucide-react';
import { Wand2 } from 'lucide-react';
import { FileUploader } from '@/components/forms/FileUploader';
import { BackgroundStudioDialog } from './BackgroundStudioDialog';
import { desnudo } from './tokens';
import { xurveyPosterUrl, type PosterPreset } from '@/lib/api/xurvey';
import { PosterPresetBar } from './PosterPresetBar';
import { useT } from '@/lib/i18n/useT';

/**
 * El cartel de este despliegue: se ve por URL y se ajusta desde aquí.
 *
 * **No lo dibuja Xotski.** Es un iframe al `poster.html` de Xurvey, igual que
 * el móvil de al lado es el enlace público real. Dibujarlo aquí daría dos
 * carteles —el que se diseña y el que se imprime— que se parecerían hasta el
 * día que dejen de parecerse, y eso se descubre con el papel ya impreso.
 *
 * Lo que se toca aquí son los `overrides.poster` de ESTE despliegue: la
 * plantilla del cartel vive en la encuesta, en Xurvey, y aquí sólo se pisa lo
 * de este evento. Vacío = lo que traiga la encuesta.
 */
export function PosterPane({
  slug,
  poster,
  onPoster,
  posterPresetId = null,
  posterPresetTocado = false,
  onAplicarPreset,
}: {
  slug: string;
  poster: Record<string, unknown>;
  onPoster: (delta: Record<string, unknown>) => void;
  /** El diseño guardado del que partió este cartel, y si se tocó después. */
  posterPresetId?: number | null;
  posterPresetTocado?: boolean;
  onAplicarPreset: (preset: PosterPreset) => void;
}) {
  const t = useT();
  const frame = useRef<HTMLIFrameElement | null>(null);
  const [exportando, setExportando] = useState(false);
  const [estudioAbierto, setEstudioAbierto] = useState(false);

  /*
   * Se reenvía TODO en cuanto el cartel dice que está listo.
   *
   * Un iframe que se recarga —al cambiar de despliegue, al recargar la
   * página— vuelve al cartel de la encuesta y perdería lo que el panel tiene
   * en pantalla sin guardar. El aviso `posterReady` es lo que permite no
   * adivinar cuándo mandar.
   */
  useEffect(() => {
    const alMensaje = (e: MessageEvent) => {
      if (e.source !== frame.current?.contentWindow) return;
      const tipo = (e.data as { type?: string })?.type;
      // El cartel avisa de que terminó de generar: el botón vuelve.
      if (tipo === 'xurvey:preview/posterExported') {
        setExportando(false);
        return;
      }
      if (tipo !== 'xurvey:preview/posterReady') return;
      frame.current?.contentWindow?.postMessage(
        { type: 'xurvey:preview/overrides', poster },
        '*',
      );
    };
    window.addEventListener('message', alMensaje);
    return () => window.removeEventListener('message', alMensaje);
  }, [poster]);

  const set = (delta: Record<string, unknown>) => {
    // Al cartel primero —un postMessage no cuesta nada y es lo que se está
    // mirando— y después al borrador, que es lo que se guardará.
    frame.current?.contentWindow?.postMessage(
      { type: 'xurvey:preview/overrides', poster: delta },
      '*',
    );
    onPoster(delta);
  };

  const texto = (clave: 'title' | 'description' | 'callToAction') => {
    const actual = (poster[clave] ?? {}) as Record<string, string>;
    return actual.es ?? actual.en ?? '';
  };

  return (
    <div className="flex flex-col gap-3">
      {/*
        Los diseños guardados, ARRIBA del cartel: es de donde se parte, igual
        que los estilos van bajo el selector de encuesta. Al aplicar uno se le
        manda al cartel embebido directamente, como cualquier otro ajuste de
        este panel, y el borrador lo recibe por `onAplicarPreset`.
      */}
      <PosterPresetBar
        poster={poster}
        seleccionado={posterPresetId}
        modificado={posterPresetTocado}
        onAplicar={(preset) => {
          frame.current?.contentWindow?.postMessage(
            { type: 'xurvey:preview/overrides', poster: preset.poster ?? {} },
            '*',
          );
          onAplicarPreset(preset);
        }}
      />
      <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-black">
        <iframe
          ref={frame}
          src={xurveyPosterUrl(slug, { preview: true })}
          title={t('designJourney.posterPreview')}
          className="block h-[420px] w-full"
        />
      </div>

      {/*
        Descargar e imprimir, aquí.
        
        El cartel vive en un iframe de otro origen, así que el panel no puede
        capturarlo: le manda el encargo y lo hace quien tiene el DOM. Los
        botones NO van dentro del cartel — esa página es la que ve el público en
        la pantalla del recinto.
      */}
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          disabled={exportando}
          onClick={() => {
            setExportando(true);
            frame.current?.contentWindow?.postMessage(
              { type: 'xurvey:preview/posterExport', formato: 'png' },
              '*',
            );
          }}
          className="flex items-center gap-1.5 rounded-md border border-[var(--color-border-default)] px-2 py-1 text-[11px] hover:border-[var(--color-accent)] disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" />
          {exportando ? t('common.loading') : t('designJourney.posterPng')}
        </button>
        <button
          type="button"
          onClick={() =>
            frame.current?.contentWindow?.postMessage(
              { type: 'xurvey:preview/posterExport', formato: 'pdf' },
              '*',
            )
          }
          className="flex items-center gap-1.5 rounded-md border border-[var(--color-border-default)] px-2 py-1 text-[11px] hover:border-[var(--color-accent)]"
        >
          <Printer className="h-3.5 w-3.5" />
          {t('designJourney.posterPdf')}
        </button>
      </div>
      <p className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
        {t('designJourney.posterDownloadHint')}
      </p>

      <p className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
        {t('designJourney.posterHint')}
      </p>

      <Campo label={t('designJourney.posterTitle')}>
        <input
          value={texto('title')}
          onChange={(e) => set({ title: { es: e.target.value, en: e.target.value } })}
          placeholder={t('designJourney.fromSurvey')}
          className={INPUT}
        />
      </Campo>
      <Campo label={t('designJourney.posterDescription')}>
        <input
          value={texto('description')}
          onChange={(e) => set({ description: { es: e.target.value, en: e.target.value } })}
          placeholder={t('designJourney.fromSurvey')}
          className={INPUT}
        />
      </Campo>
      <Campo label={t('designJourney.posterCta')}>
        <input
          value={texto('callToAction')}
          onChange={(e) => set({ callToAction: { es: e.target.value, en: e.target.value } })}
          placeholder={t('designJourney.fromSurvey')}
          className={INPUT}
        />
      </Campo>

      {/*
        El fondo del cartel: subir uno o diseñarlo.
        
        El MISMO estudio que el fondo del formulario, y a propósito: quien ya
        sabe diseñar uno no tiene que aprender otra herramienta, y lo que
        guarda es el mismo valor CSS. Los colores que propone el motor se
        aplican AL CARTEL —su texto y su color de fondo—, nunca al formulario:
        repintar la encuesta desde una pestaña que dice «cartel» sería tocar
        algo que no se está mirando.
      */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] text-[var(--color-fg-muted)]">
          {t('designJourney.posterBackgroundImage')}
        </span>
        <FileUploader
          value={desnudo((poster.backgroundImage as string) ?? null)}
          onChange={(url) =>
            set({
              // Envuelto en `url(...)`: es lo que espera el renderer, que
              // comparte este campo con degradados y colores.
              backgroundImage: url ? `url("${url}")` : '',
              backgroundSize: 'cover',
            })
          }
        />
        <button
          type="button"
          onClick={() => setEstudioAbierto(true)}
          className="flex items-center justify-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] py-2 text-[11px] font-bold text-[var(--color-xk-lavender)] transition-colors hover:border-[var(--color-accent-ink)] hover:text-[var(--color-accent-ink)]"
        >
          <Wand2 className="h-3.5 w-3.5" />
          {t('designJourney.designBackground')}
        </button>
      </div>

      <FilaColorSimple
        label={t('designJourney.posterBackground')}
        value={(poster.backgroundColor as string) ?? ''}
        onChange={(v) => set({ backgroundColor: v })}
      />
      <FilaColorSimple
        label={t('designJourney.posterText')}
        value={(poster.textColor as string) ?? ''}
        onChange={(v) => set({ textColor: v })}
      />
      <FilaColorSimple
        label={t('designJourney.posterQrColor')}
        value={(poster.qrColor as string) ?? ''}
        onChange={(v) => set({ qrColor: v })}
      />
      <FilaColorSimple
        label={t('designJourney.posterQrBackground')}
        value={(poster.qrBackground as string) ?? ''}
        onChange={(v) => set({ qrBackground: v })}
      />

      {/*
        El logo en el centro del QR.
        
        Se puede porque el código se dibuja con corrección de errores ALTA y el
        logo ocupa un quinto del lado: por debajo de eso el lector reconstruye
        lo que el logo tapa. Subirlo más grande sí rompería el escaneo, y por
        eso el tamaño no se ofrece aquí.
      */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] text-[var(--color-fg-muted)]">
          {t('designJourney.posterQrLogo')}
        </span>
        <FileUploader
          value={(poster.qrLogoUrl as string) || null}
          onChange={(url) => set({ qrLogoUrl: url ?? '' })}
          preset="logo"
        />
        <span className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
          {t('designJourney.posterQrLogoHint')}
        </span>
      </div>
      {estudioAbierto ? (
        <BackgroundStudioDialog
          open
          onOpenChange={setEstudioAbierto}
          onUse={(url, tokens) => {
            set({
              backgroundImage: `url("${url}")`,
              backgroundSize: 'cover',
              /*
               * Los colores que propone el motor, si se pidieron.
               *
               * Sólo el texto y el color de fondo. Los del QR se dejan como
               * están a propósito: su contraste es lo único de este cartel que
               * no se puede arriesgar —por debajo de 3:1 hay móviles que no lo
               * leen— y una paleta bonita no es garantía de eso.
               */
              ...(tokens?.text ? { textColor: tokens.text } : {}),
              ...(tokens?.bg ? { backgroundColor: tokens.bg } : {}),
            });
            setEstudioAbierto(false);
          }}
        />
      ) : null}
    </div>
  );
}

const INPUT =
  'h-8 w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-default)] px-2 text-[12px]';

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] text-[var(--color-fg-muted)]">{label}</span>
      {children}
    </label>
  );
}

/**
 * Un color del cartel. Sin «heredado» como los tokens: aquí el vacío es «lo que
 * traiga la encuesta», y eso lo pinta el propio cartel, no este panel.
 */
function FilaColorSimple({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-[12px]">
      <input
        type="color"
        value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#ffffff'}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className="h-6 w-6 cursor-pointer rounded border border-[var(--color-border-default)] p-0"
      />
      <span className="flex-1">{label}</span>
      <span className="font-mono text-[10px] text-[var(--color-fg-muted)]">{value || '—'}</span>
    </label>
  );
}
